/** Address-tab map preview. Coordinates are data, never guessed. */
import { useCallback, useMemo, useState } from "react";
import { MapFrame } from "@v0y4ge/jrni-ui";
import GoogleMap from "../../components/maps/GoogleMap";
import { useT } from "../../lib/i18n";

export interface LocationMapProps {
  latitude?: number | null;
  longitude?: number | null;
  label?: string;
  caption?: string;
  height?: number;
  action?: React.ReactNode;
}

export default function LocationMap({ latitude, longitude, label, caption, height = 220, action }: LocationMapProps) {
  const t = useT();
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const hasCoords = typeof latitude === "number" && typeof longitude === "number"
    && Number.isFinite(latitude) && Number.isFinite(longitude);
  const markers = useMemo(() => [{ id: "location", latitude: latitude as number, longitude: longitude as number, label }], [label, latitude, longitude]);
  const onStatusChange = useCallback((nextStatus: "loading" | "ready" | "error", message?: string) => {
    setStatus(nextStatus);
    setError(message ?? null);
  }, []);

  return (
    <MapFrame
      state={hasCoords ? status : "empty"}
      height={height}
      ariaLabel={t("locations.map.locationMap", "Location map")}
      caption={caption}
      actions={action}
      loadingLabel={t("locations.map.loadingMap", "Loading map…")}
      errorTitle={t("locations.map.theMapCouldnTLoad", "The map couldn't load.")}
      errorDescription={error ?? "Google Maps could not be loaded."}
      emptyTitle={t("locations.map.notLocatedYet", "Not located yet")}
      emptyDescription={t("locations.map.thisAddressHasNoCoordinates", "This address has no coordinates on record, so there is nothing to pin. Resolve it from the address to place it on the map.")}
    >
      {hasCoords ? (
        <div inert={status !== "ready"}>
        <GoogleMap
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          mapId={import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID"}
          markers={markers}
          height={height}
          onStatusChange={onStatusChange}
        />
        </div>
      ) : null}
    </MapFrame>
  );
}
